import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { logger } from '../utils/core/logger';
import type { WidgetThemeVars } from '../utils/ui/widgetTheme';
import SvgMaskIcon from './SvgMaskIcon';

interface EvaluationPopupProps {
    isOpen: boolean;
    onClose: (result: EvaluationPopupResult) => void;
    rating: 'thumbs_up' | 'thumbs_down';
    themeVars: WidgetThemeVars;
    zIndex: number;
}

export interface EvaluationPopupResult {
    rating: 'thumbs_up' | 'thumbs_down';
    finalRating:
        | 'Accurate & OK'
        | 'Accurate but Not OK'
        | 'Not Accurate but OK'
        | 'Not Accurate & Not OK';
    comment?: string;
    hasHallucination?: boolean;
    cancelled: boolean;
}

const EvaluationPopup: React.FC<EvaluationPopupProps> = ({
    isOpen,
    onClose,
    rating,
    themeVars,
    zIndex,
}) => {
    const [comment, setComment] = useState('');
    const [hallucinationChoice, setHallucinationChoice] = useState<
        'none' | 'no_hallucination' | 'hallucination'
    >('none');
    const [showError, setShowError] = useState(false);
    const [showChoiceError, setShowChoiceError] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setComment('');
            setHallucinationChoice('none');
            setShowError(false);
            setShowChoiceError(false);
        }
    }, [isOpen]);

    const handleSubmit = () => {
        const trimmedComment = comment.trim();
        let hasError = false;

        // Validate comment is mandatory
        if (!trimmedComment) {
            setShowError(true);
            hasError = true;
        } else {
            setShowError(false);
        }

        // Validate hallucination choice is mandatory
        if (hallucinationChoice === 'none') {
            setShowChoiceError(true);
            hasError = true;
        } else {
            setShowChoiceError(false);
        }

        if (hasError) {
            return;
        }

        const finalRating =
            hallucinationChoice === 'hallucination'
                ? 'Not Accurate & Not OK'
                : 'Accurate but Not OK';
        const hasHallucination = hallucinationChoice === 'hallucination';

        onClose({
            rating,
            finalRating,
            comment: trimmedComment,
            hasHallucination,
            cancelled: false,
        });
    };

    const handleCancel = () => {
        onClose({
            rating,
            finalRating: 'Accurate but Not OK', // Default when cancelled
            cancelled: true,
        });
    };

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(e.target.value);
        if (e.target.value.trim()) {
            setShowError(false);
        }
    };

    const handleChoiceChange = (value: 'no_hallucination' | 'hallucination') => {
        setHallucinationChoice(value);
        setShowChoiceError(false);
    };

    if (!isOpen) {
        logger.debug('EvaluationPopup: isOpen=false, not rendering');
        return null;
    }

    logger.debug('EvaluationPopup: isOpen=true, rendering popup');
    logger.debug('EvaluationPopup: document.body exists:', !!document.body);

    // Add CSS animations as a style tag
    const animationStyles = `
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        @keyframes slideInFromBottom {
            from { 
                opacity: 0;
                transform: translateY(20px) scale(0.95);
            }
            to { 
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }
    `;

    const popupContent = (
        <>
            <style>{animationStyles}</style>
            <div
                className="inboundx-evaluation-popup"
                data-rw-portal-root=""
                style={{
                    ...themeVars,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    zIndex,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px 16px',
                    animation: 'fadeIn 0.2s ease-out',
                }}
            >
                {/* Backdrop */}
                <div
                    className="inboundx-evaluation-overlay"
                    onClick={handleCancel}
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'var(--rw-overlay-backdrop)',
                        backdropFilter: 'blur(4px)',
                    }}
                />

                {/* Modal Content */}
                <div
                    className="inboundx-evaluation-content"
                    style={{
                        position: 'relative',
                        backgroundColor: 'var(--rw-surface-panel)',
                        borderRadius: 'var(--rw-radius-shell-panel)',
                        width: '100%',
                        maxWidth: '700px',
                        minWidth: '600px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        animation: 'slideInFromBottom 0.3s ease-out',
                        fontFamily: 'var(--rw-font-family)',
                    }}
                >
                    {/* Header */}
                    <div
                        className="inboundx-evaluation-header"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '24px',
                            borderBottom: '1px solid var(--rw-border-subtle)',
                        }}
                    >
                        <div
                            className="inboundx-evaluation-icon"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                                backgroundColor: 'var(--rw-surface-secondary)',
                                borderRadius: 'var(--rw-radius-card)',
                                color: 'var(--rw-icon-muted)',
                                flexShrink: 0,
                            }}
                        >
                            <SvgMaskIcon
                                viewBox="0 0 24 24"
                                inner='<path d="M9.5 14.25C9.5 12.9 10.6 11.8 12 11.8C13.4 11.8 14.5 12.9 14.5 14.25M12 8.5V9.5M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
                                width={24}
                                height={24}
                                color="currentColor"
                            />
                        </div>
                        <h3
                            style={{
                                fontSize: '18px',
                                fontWeight: 'var(--rw-font-weight-strong)',
                                color: 'var(--rw-text-primary)',
                                flex: 1,
                                margin: 0,
                            }}
                        >
                            Help us get it right
                        </h3>
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="inboundx-evaluation-close"
                            aria-label="Close dialog"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                borderRadius: 'var(--rw-radius-shell-compact)',
                                color: 'var(--rw-icon-muted)',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                flexShrink: 0,
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    'var(--rw-surface-secondary)';
                                e.currentTarget.style.color = 'var(--rw-text-primary)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = 'var(--rw-icon-muted)';
                            }}
                        >
                            <SvgMaskIcon
                                viewBox="0 0 20 20"
                                inner='<path d="M15 5L5 15M5 5L15 15" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
                                width={20}
                                height={20}
                                color="currentColor"
                            />
                        </button>
                    </div>

                    {/* Form Content */}
                    <div className="inboundx-evaluation-form" style={{ padding: '24px' }}>
                        {/* Welcome Message */}
                        <div
                            className="inboundx-evaluation-welcome"
                            style={{
                                background:
                                    'linear-gradient(135deg, var(--rw-surface-secondary) 0%, var(--rw-surface-panel) 100%)',
                                borderRadius: 'var(--rw-radius-card)',
                                padding: '16px',
                                marginBottom: '24px',
                                border: '1px solid var(--rw-border-subtle)',
                            }}
                        >
                            <h5
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 'var(--rw-font-weight-medium)',
                                    color: 'var(--rw-text-primary)',
                                    marginBottom: '12px',
                                    textAlign: 'left',
                                    margin: '0 0 12px 0',
                                }}
                            >
                                🎯 Tell us the answer you expected
                            </h5>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        textAlign: 'left',
                                        color: 'var(--rw-text-secondary)',
                                    }}
                                >
                                    Your feedback goes straight to the team that improves this
                                    assistant. The more precisely you describe the problem, the
                                    faster we can fix it.
                                </p>
                                <p
                                    style={{
                                        margin: 0,
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        fontWeight: 'var(--rw-font-weight-medium)',
                                        textAlign: 'left',
                                        color: 'var(--rw-text-primary)',
                                    }}
                                >
                                    Most important: tell us what the correct answer should have
                                    been. Without it, we can't fix the response.
                                </p>
                            </div>
                        </div>

                        {/* Comment Field */}
                        <div className="inboundx-evaluation-field" style={{ marginBottom: '24px' }}>
                            <label
                                htmlFor="inboundx-evaluation-comment"
                                style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 'var(--rw-font-weight-medium)',
                                    color: 'var(--rw-text-primary)',
                                    marginBottom: '8px',
                                }}
                            >
                                What went wrong — and what's the correct answer?
                            </label>
                            <textarea
                                id="inboundx-evaluation-comment"
                                className="inboundx-evaluation-textarea"
                                style={{
                                    width: '100%',
                                    minHeight: '100px',
                                    padding: '12px 16px',
                                    border: showError
                                        ? '2px solid #ef4444'
                                        : '2px solid var(--rw-border-subtle)',
                                    borderRadius: 'var(--rw-radius-shell-compact)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--rw-font-family)',
                                    resize: 'vertical',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: 'var(--rw-surface-panel)',
                                    color: 'var(--rw-text-primary)',
                                    lineHeight: '1.6',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                                placeholder="Describe the problem, then tell us the answer you expected. Be as specific as possible."
                                rows={4}
                                value={comment}
                                onChange={handleCommentChange}
                                onFocus={(e) => {
                                    if (!showError) {
                                        e.target.style.borderColor =
                                            'var(--rw-border-selection-accent)';
                                        e.target.style.boxShadow = '0 0 0 3px var(--rw-focus-ring)';
                                    }
                                }}
                                onBlur={(e) => {
                                    if (!showError) {
                                        e.target.style.borderColor = 'var(--rw-border-subtle)';
                                        e.target.style.boxShadow = 'none';
                                    }
                                }}
                                required
                            />
                            {showError && (
                                <div
                                    className="inboundx-evaluation-field-error"
                                    style={{
                                        marginTop: '8px',
                                        fontSize: '14px',
                                        color: '#dc2626',
                                        fontWeight: 'var(--rw-font-weight-medium)',
                                    }}
                                >
                                    Please tell us what the correct answer should have been
                                </div>
                            )}
                        </div>

                        {/* Issue Type Field */}
                        <div className="inboundx-evaluation-field" style={{ marginBottom: '32px' }}>
                            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                                <legend
                                    id="inboundx-evaluation-field-label"
                                    className="inboundx-evaluation-field-label"
                                    style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 'var(--rw-font-weight-medium)',
                                        color: 'var(--rw-text-primary)',
                                        marginBottom: '12px',
                                    }}
                                >
                                    What type of issue was this?
                                </legend>
                                <div
                                    className="inboundx-evaluation-radio-group"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px',
                                        width: '100%',
                                    }}
                                >
                                    <label
                                        className="inboundx-evaluation-radio-option"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px',
                                            padding: '16px',
                                            border:
                                                hallucinationChoice === 'no_hallucination'
                                                    ? '2px solid var(--rw-border-selection-accent)'
                                                    : '2px solid var(--rw-border-subtle)',
                                            borderRadius: 'var(--rw-radius-shell-compact)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            backgroundColor:
                                                hallucinationChoice === 'no_hallucination'
                                                    ? 'var(--rw-bg-selection-accent)'
                                                    : 'var(--rw-surface-panel)',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (hallucinationChoice !== 'no_hallucination') {
                                                e.currentTarget.style.backgroundColor =
                                                    'var(--rw-surface-secondary)';
                                                e.currentTarget.style.borderColor =
                                                    'var(--rw-border-subtle)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (hallucinationChoice !== 'no_hallucination') {
                                                e.currentTarget.style.backgroundColor =
                                                    'var(--rw-surface-panel)';
                                                e.currentTarget.style.borderColor =
                                                    'var(--rw-border-subtle)';
                                            }
                                        }}
                                    >
                                        <div
                                            className="inboundx-evaluation-radio-button"
                                            style={{
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                marginTop: '2px',
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="hallucination-choice"
                                                value="no_hallucination"
                                                checked={hallucinationChoice === 'no_hallucination'}
                                                onChange={(e) =>
                                                    handleChoiceChange(
                                                        e.target.value as 'no_hallucination'
                                                    )
                                                }
                                                style={{
                                                    position: 'absolute',
                                                    width: '1px',
                                                    height: '1px',
                                                    padding: 0,
                                                    margin: '-1px',
                                                    overflow: 'hidden',
                                                    clip: 'rect(0, 0, 0, 0)',
                                                    whiteSpace: 'nowrap',
                                                    border: 0,
                                                }}
                                            />
                                            <div
                                                className="inboundx-evaluation-radio-indicator"
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    border:
                                                        hallucinationChoice === 'no_hallucination'
                                                            ? '2px solid var(--rw-border-selection-accent)'
                                                            : '2px solid var(--rw-border-subtle)',
                                                    borderRadius: '50%',
                                                    transition: 'all 0.2s ease',
                                                    backgroundColor:
                                                        hallucinationChoice === 'no_hallucination'
                                                            ? 'var(--rw-bg-action-primary)'
                                                            : 'var(--rw-surface-panel)',
                                                    position: 'relative',
                                                }}
                                            >
                                                {hallucinationChoice === 'no_hallucination' && (
                                                    <div
                                                        style={{
                                                            width: '6px',
                                                            height: '6px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%',
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className="inboundx-evaluation-radio-content"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <div
                                                className="inboundx-evaluation-radio-title"
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 'var(--rw-font-weight-medium)',
                                                    color: 'var(--rw-text-primary)',
                                                    lineHeight: '1.25',
                                                }}
                                            >
                                                Response wasn't helpful
                                            </div>
                                            <div
                                                className="inboundx-evaluation-radio-desc"
                                                style={{
                                                    fontSize: '14px',
                                                    color: 'var(--rw-text-secondary)',
                                                    lineHeight: '1.25',
                                                }}
                                            >
                                                Information was accurate but didn't answer your
                                                question
                                            </div>
                                        </div>
                                    </label>
                                    <label
                                        className="inboundx-evaluation-radio-option"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: '12px',
                                            padding: '16px',
                                            border:
                                                hallucinationChoice === 'hallucination'
                                                    ? '2px solid var(--rw-border-selection-accent)'
                                                    : '2px solid var(--rw-border-subtle)',
                                            borderRadius: 'var(--rw-radius-shell-compact)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            backgroundColor:
                                                hallucinationChoice === 'hallucination'
                                                    ? 'var(--rw-bg-selection-accent)'
                                                    : 'var(--rw-surface-panel)',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (hallucinationChoice !== 'hallucination') {
                                                e.currentTarget.style.backgroundColor =
                                                    'var(--rw-surface-secondary)';
                                                e.currentTarget.style.borderColor =
                                                    'var(--rw-border-subtle)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (hallucinationChoice !== 'hallucination') {
                                                e.currentTarget.style.backgroundColor =
                                                    'var(--rw-surface-panel)';
                                                e.currentTarget.style.borderColor =
                                                    'var(--rw-border-subtle)';
                                            }
                                        }}
                                    >
                                        <div
                                            className="inboundx-evaluation-radio-button"
                                            style={{
                                                position: 'relative',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                marginTop: '2px',
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="hallucination-choice"
                                                value="hallucination"
                                                checked={hallucinationChoice === 'hallucination'}
                                                onChange={(e) =>
                                                    handleChoiceChange(
                                                        e.target.value as 'hallucination'
                                                    )
                                                }
                                                style={{
                                                    position: 'absolute',
                                                    width: '1px',
                                                    height: '1px',
                                                    padding: 0,
                                                    margin: '-1px',
                                                    overflow: 'hidden',
                                                    clip: 'rect(0, 0, 0, 0)',
                                                    whiteSpace: 'nowrap',
                                                    border: 0,
                                                }}
                                            />
                                            <div
                                                className="inboundx-evaluation-radio-indicator"
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    border:
                                                        hallucinationChoice === 'hallucination'
                                                            ? '2px solid var(--rw-border-selection-accent)'
                                                            : '2px solid var(--rw-border-subtle)',
                                                    borderRadius: '50%',
                                                    transition: 'all 0.2s ease',
                                                    backgroundColor:
                                                        hallucinationChoice === 'hallucination'
                                                            ? 'var(--rw-bg-action-primary)'
                                                            : 'var(--rw-surface-panel)',
                                                    position: 'relative',
                                                }}
                                            >
                                                {hallucinationChoice === 'hallucination' && (
                                                    <div
                                                        style={{
                                                            width: '6px',
                                                            height: '6px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%',
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className="inboundx-evaluation-radio-content"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <div
                                                className="inboundx-evaluation-radio-title"
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 'var(--rw-font-weight-medium)',
                                                    color: 'var(--rw-text-primary)',
                                                    lineHeight: '1.25',
                                                }}
                                            >
                                                Information was incorrect
                                            </div>
                                            <div
                                                className="inboundx-evaluation-radio-desc"
                                                style={{
                                                    fontSize: '14px',
                                                    color: 'var(--rw-text-secondary)',
                                                    lineHeight: '1.25',
                                                }}
                                            >
                                                AI provided inaccurate or made-up information
                                            </div>
                                        </div>
                                    </label>
                                </div>
                                {showChoiceError && (
                                    <div
                                        className="inboundx-evaluation-field-error"
                                        style={{
                                            marginTop: '8px',
                                            fontSize: '14px',
                                            color: '#dc2626',
                                            fontWeight: '500',
                                        }}
                                    >
                                        Please select the type of issue
                                    </div>
                                )}
                            </fieldset>
                        </div>

                        {/* Action Buttons */}
                        <div
                            className="inboundx-evaluation-actions"
                            style={{
                                display: 'flex',
                                gap: '12px',
                                justifyContent: 'flex-end',
                                flexDirection: 'row',
                            }}
                        >
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="inboundx-evaluation-button inboundx-evaluation-button-secondary"
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: 'var(--rw-radius-shell-compact)',
                                    fontSize: '14px',
                                    fontWeight: 'var(--rw-font-weight-medium)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    minWidth: '100px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid var(--rw-border-subtle)',
                                    backgroundColor: 'var(--rw-surface-panel)',
                                    color: 'var(--rw-icon-muted)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        'var(--rw-surface-secondary)';
                                    e.currentTarget.style.borderColor = 'var(--rw-border-subtle)';
                                    e.currentTarget.style.color = 'var(--rw-text-primary)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        'var(--rw-surface-panel)';
                                    e.currentTarget.style.borderColor = 'var(--rw-border-subtle)';
                                    e.currentTarget.style.color = 'var(--rw-icon-muted)';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="inboundx-evaluation-button inboundx-evaluation-button-primary"
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: 'var(--rw-radius-shell-compact)',
                                    fontSize: '14px',
                                    fontWeight: 'var(--rw-font-weight-medium)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    minWidth: '100px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid var(--rw-bg-action-primary)',
                                    backgroundColor: 'var(--rw-bg-action-primary)',
                                    color: 'var(--rw-text-on-action-primary)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                    e.currentTarget.style.boxShadow =
                                        '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                onMouseDown={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                Submit Feedback
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    // Use portal to render directly in document.body to avoid container constraints
    if (typeof document !== 'undefined' && document.body) {
        logger.debug('EvaluationPopup: Using portal to render in document.body');
        return createPortal(popupContent, document.body);
    } else {
        logger.debug('EvaluationPopup: Fallback to normal render');
        return popupContent;
    }
};

export default EvaluationPopup;
